import type {
  DeliveryStatus,
  DisabledReason,
  EndpointHealth,
  WebhookDelivery,
  WebhookDeliveryDetail,
  WebhookEndpoint,
  WebhookEventType
} from "@/vuex/modules/integrationWebhooks";

// Copy and small pure helpers shared by the hub's tabs, in the shape of
// Zapier/zapierPanel.ts.

const HEALTH_STYLES: Record<EndpointHealth, { label: string; pill: string }> = {
  healthy: { label: "Healthy", pill: "bg-green-50 text-green-700" },
  degraded: { label: "Degraded", pill: "bg-yellow-50 text-yellow-600" },
  failing: { label: "Failing", pill: "bg-red-50 text-red-700" },
  disabled: { label: "Disabled", pill: "bg-gray-200 text-gray-700" },
  paused: { label: "Paused", pill: "bg-blue-50 text-blue-700" }
};

export function healthStyle(health: string) {
  return (
    HEALTH_STYLES[health as EndpointHealth] ?? {
      label: health,
      pill: "bg-gray-100 text-gray-600"
    }
  );
}

const DISABLED_REASON_COPY: Record<DisabledReason, string> = {
  admin: "Disabled by an admin",
  failing: "Disabled after failing for 5 days",
  receiver_gone: "Disabled because your server told us to stop sending (410)",
  integration_off: "Disabled because webhooks were turned off"
};

export function disabledReasonCopy(reason: DisabledReason | null): string {
  return (reason && DISABLED_REASON_COPY[reason]) || "Disabled";
}

export function endpointLabel(endpoint: WebhookEndpoint): string {
  return endpoint.description?.trim() || endpoint.destination.host;
}

export function formatSuccessRate(rate: number | null | undefined): string {
  if (rate === null || rate === undefined) return "—";
  const percent = rate * 100;
  return `${percent === 100 || percent === 0 ? percent.toFixed(0) : percent.toFixed(1)}%`;
}

export interface EventTypeGroup {
  group: string;
  eventTypes: WebhookEventType[];
}

// Catalog order is kept, within and across groups.
export function groupEventTypes(
  eventTypes: WebhookEventType[]
): EventTypeGroup[] {
  const groups = new Map<string, EventTypeGroup>();
  eventTypes.forEach((eventType) => {
    if (!groups.has(eventType.group)) {
      groups.set(eventType.group, { group: eventType.group, eventTypes: [] });
    }
    groups.get(eventType.group)!.eventTypes.push(eventType);
  });
  return [...groups.values()];
}

const NOT_DELIVERABLE_COPY: Record<string, string> = {
  feature_off: "Webhooks aren't available on this account.",
  account_inactive: "Your account is inactive, so nothing can be sent.",
  integration_off:
    "Webhooks are turned off. Turn them on from the Webhooks card in the Marketplace.",
  endpoint_paused: "This endpoint is paused. Resume it, then try again.",
  endpoint_inactive: "This endpoint is disabled. Enable it, then try again."
};

export const READ_ONLY_COPY =
  "Your account is read-only, so webhooks can't be changed.";

interface ApiErrorBody {
  message?: string;
  error_code?: string;
  errors?: Record<string, string[]>;
  reason?: string;
}

export interface ApiError {
  status: number | null;
  code: string | null;
  message: string;
  fieldErrors: Record<string, string[]>;
}

// Turns a rejected request into plain copy. The API's own messages are
// written for admins and never repeat the URL, so they're used as they come,
// except where the hub has a more specific line.
export function apiError(error: unknown, fallback: string): ApiError {
  const response = (
    error as { response?: { status?: number; data?: ApiErrorBody } }
  )?.response;
  const body: ApiErrorBody =
    response?.data && typeof response.data === "object" ? response.data : {};
  const code = body.error_code ?? null;

  let message = body.message || fallback;
  if (code === "not_deliverable" && body.reason) {
    message = NOT_DELIVERABLE_COPY[body.reason] ?? message;
  } else if (code === "account_read_only") {
    message = READ_ONLY_COPY;
  } else if (code === "not_found") {
    message = "It no longer exists. Reload the page.";
  } else if (response?.status === 403 && !code) {
    message = "You don't have permission to do that.";
  } else if (!response) {
    message = fallback;
  }

  return {
    status: response?.status ?? null,
    code,
    message,
    fieldErrors: body.errors ?? {}
  };
}

// Given in the drawer next to the secret (ENG-888). The libraries verify the
// signature and reject a timestamp more than 5 minutes old.
export const SIGNATURE_WARNING =
  "If your endpoint doesn't verify the webhook-signature header, it will accept forged requests from anyone who knows its URL.";

export const ROTATION_EXPLANATION =
  "Rotating creates a new secret. For the next 24 hours Sera signs every request with both the old and the new secret, so you have time to deploy the new one. After that only the new secret works.";

export interface VerificationSnippet {
  key: string;
  label: string;
  install: string;
  code: string;
}

export const VERIFICATION_SNIPPETS: VerificationSnippet[] = [
  {
    key: "node",
    label: "Node.js",
    install: "npm install standardwebhooks",
    code: `import { Webhook } from "standardwebhooks";

const wh = new Webhook(process.env.SERA_WEBHOOK_SECRET); // whsec_…

// Use the raw request body exactly as received, not re-serialized JSON.
// verify() throws if the signature is wrong or the timestamp is more than
// 5 minutes old.
const event = wh.verify(rawBody, {
  "webhook-id": req.headers["webhook-id"],
  "webhook-timestamp": req.headers["webhook-timestamp"],
  "webhook-signature": req.headers["webhook-signature"]
});`
  },
  {
    key: "python",
    label: "Python",
    install: "pip install standardwebhooks",
    code: `import os
from standardwebhooks.webhooks import Webhook

wh = Webhook(os.environ["SERA_WEBHOOK_SECRET"])  # whsec_…

# Use the raw request body exactly as received. verify() raises if the
# signature is wrong or the timestamp is more than 5 minutes old.
event = wh.verify(request.get_data(), {
    "webhook-id": request.headers["webhook-id"],
    "webhook-timestamp": request.headers["webhook-timestamp"],
    "webhook-signature": request.headers["webhook-signature"],
})`
  },
  {
    key: "ruby",
    label: "Ruby",
    install: "gem install standardwebhooks",
    code: `require "standardwebhooks"

wh = StandardWebhooks::Webhook.new(ENV.fetch("SERA_WEBHOOK_SECRET")) # whsec_…

# Use the raw request body exactly as received. verify raises if the
# signature is wrong or the timestamp is more than 5 minutes old.
event = wh.verify(request.raw_post, {
  "webhook-id" => request.headers["webhook-id"],
  "webhook-timestamp" => request.headers["webhook-timestamp"],
  "webhook-signature" => request.headers["webhook-signature"]
})`
  }
];

export const DELIVERY_STATUSES: DeliveryStatus[] = [
  "succeeded",
  "failed",
  "retrying",
  "pending",
  "cancelled"
];

const DELIVERY_STATUS_STYLES: Record<
  DeliveryStatus,
  { label: string; pill: string }
> = {
  succeeded: { label: "Succeeded", pill: "bg-green-50 text-green-700" },
  failed: { label: "Failed", pill: "bg-red-50 text-red-700" },
  retrying: { label: "Retrying", pill: "bg-yellow-50 text-yellow-600" },
  pending: { label: "Pending", pill: "bg-blue-50 text-blue-700" },
  cancelled: { label: "Cancelled", pill: "bg-gray-100 text-gray-600" }
};

export function deliveryStatusStyle(status: string) {
  return (
    DELIVERY_STATUS_STYLES[status as DeliveryStatus] ?? {
      label: status,
      pill: "bg-gray-100 text-gray-600"
    }
  );
}

// Retry is offered where the API allows it: any delivery that has finished
// (succeeded, failed or cancelled). Pending and retrying ones are still in
// flight and answer 409.
export function canRetry(status: DeliveryStatus): boolean {
  return (
    status === "succeeded" || status === "failed" || status === "cancelled"
  );
}

// A "Send test event" delivery: its body's origin is "test". The row's own
// `test` field says the same for lists, which carry no body.
export function isTestDelivery(
  delivery: WebhookDelivery | WebhookDeliveryDetail
): boolean {
  const body = (delivery as WebhookDeliveryDetail).request?.body as
    { origin?: string } | undefined;
  return body?.origin === "test" || !!delivery.test;
}

export function deliveryEndpointLabel(delivery: WebhookDelivery): string {
  return delivery.endpoint.description?.trim() || delivery.endpoint.host;
}
